/**
 * The step after the cell edit (masterplan credit-limits-v3, B4; E3, E6, E11, E19, E20, E23, E34): the new limit,
 * the mandatory reason, the approval chain as a PREVIEW («Approval by …», computed from the approval rules of
 * `GET /credit-limits/config` by `requests/chainPreview.ts`) and the insurance hint (E19, never a block). «Send
 * request» calls `POST /credit-limits/requests`; the chain in the answer is the binding one. 400 (same limit, missing
 * reason), 403 (no role) and 409 (open request) come back as a message in the dialog.
 */
import { useEffect, useId, useMemo, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { creditLimitsApi } from '../../../services/creditLimitsApi';
import type { CreditLimitConfig, CreditLimitRequestDto, OverviewRow } from '../../../types/creditLimits';
import { chainText, previewChain } from '../requests/chainPreview';
import { MAX_COMMENT_LENGTH, apiErrorText } from '../requests/requestRules';
import { insuranceHint } from './limitEdit';
import { OverviewDialog } from './OverviewDialog';
import { formatChf } from './overviewColumns';

interface Props {
    row: OverviewRow;
    /** The new limit, already parsed (`cellEdit.parseLimitInput`). */
    amountChf: number;
    /** The signed-in person (`roles.email`). */
    requesterEmail: string;
    onClose: () => void;
    onCreated: (request: CreditLimitRequestDto) => void;
}

function limitWords(chf: number): string {
    return chf <= 1 ? `No limit (CHF ${formatChf(chf)})` : `CHF ${formatChf(chf)}`;
}

export function LimitRequestDialog({ row, amountChf, requesterEmail, onClose, onCreated }: Props) {
    const [config, setConfig] = useState<CreditLimitConfig | null>(null);
    const [configError, setConfigError] = useState<string | null>(null);
    const [reason, setReason] = useState('');
    const [touched, setTouched] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const reasonId = useId();

    useEffect(() => {
        let live = true;
        creditLimitsApi.getConfig()
            .then(c => { if (live) setConfig(c); })
            .catch(err => { if (live) setConfigError(apiErrorText(err, 'The approval rules could not be loaded')); });
        return () => { live = false; };
    }, []);

    const preview = useMemo(() => (config ? previewChain({
        requesterEmail,
        requestedLimitChf: amountChf,
        salesManagerName: row.salesManagerName,
        tiers: config.tiers ?? [],
        approvers: config.approvers ?? [],
        finance: config.finance ?? [],
        defaultTopEmail: config.settings?.defaultTopEmail ?? null,
    }) : null), [config, requesterEmail, amountChf, row.salesManagerName]);

    const hint = insuranceHint(amountChf, row.creditInsuranceEur);
    const reasonMissing = reason.trim() === '';

    const send = async () => {
        setTouched(true);
        if (reasonMissing) return;
        setBusy(true);
        setError(null);
        try {
            const created = await creditLimitsApi.createRequest({ customerCode: row.customerCode, requestedLimitChf: amountChf, reason: reason.trim() });
            onCreated(created);
        } catch (err) {
            setError(apiErrorText(err, 'The request could not be sent.'));
            setBusy(false);
        }
    };

    const footer = (
        <>
            <button type="button" className="btn secondary sm" onClick={onClose} disabled={busy}>Cancel</button>
            <button type="button" className="btn primary sm" onClick={send} disabled={busy}>{busy ? 'Sending…' : 'Send request'}</button>
        </>
    );

    return (
        <OverviewDialog title="Change limit" subtitle={`${row.customerName ?? row.customerCode} (${row.customerCode})`} onClose={onClose} footer={footer} width={620}>
            <div style={{ display: 'grid', gap: 12 }}>
                <p style={{ margin: 0 }} className="tnum">
                    {row.hasLimit && row.creditLimitChf !== null ? `CHF ${formatChf(row.creditLimitChf)}` : 'No limit'} → <b>{limitWords(amountChf)}</b>
                </p>

                <div>
                    <p style={{ margin: 0, fontWeight: 600, color: 'var(--text-strong)' }}>
                        Approval chain <span className="muted" style={{ fontWeight: 450 }}>(preview{preview?.ok ? `, tier ${preview.tierNo}` : ''})</span>
                    </p>
                    {!config && !configError && <p className="muted" role="status" style={{ margin: 0 }}>Loading the approval rules…</p>}
                    {configError && <p className="muted" style={{ margin: 0 }}>{configError}. The request can still be sent; the server sets the chain.</p>}
                    {preview && <p style={{ margin: 0 }} data-testid="chain-preview">{chainText(preview, requesterEmail)}</p>}
                    <p className="muted" style={{ margin: '2px 0 0' }}>A preview from the approval rules. The chain of the sent request is binding.</p>
                </div>

                {hint && (
                    <p className="hint" role="note" style={{ margin: 0 }}>
                        <AlertTriangle className="i sm" aria-hidden="true" />
                        {hint}
                    </p>
                )}

                <div style={{ display: 'grid', gap: 4 }}>
                    <label htmlFor={reasonId} style={{ fontWeight: 600, color: 'var(--text-strong)' }}>Reason (required)</label>
                    <textarea
                        id={reasonId}
                        className="input"
                        style={{ height: 'auto', minHeight: 88, padding: '8px 12px' }}
                        rows={3}
                        maxLength={MAX_COMMENT_LENGTH}
                        value={reason}
                        aria-required="true"
                        aria-invalid={touched && reasonMissing}
                        onChange={e => setReason(e.target.value)}
                    />
                    {touched && reasonMissing && <p className="hint" role="alert" style={{ margin: 0 }}>A reason is required.</p>}
                </div>

                {error && <div className="banner error" role="alert"><p>{error}</p></div>}
            </div>
        </OverviewDialog>
    );
}
